import LegalLayout from "@/components/LegalLayout";

function H2({ children }) {
  return (
    <h2 className="mt-10 font-heading text-2xl md:text-3xl"
      style={{ fontVariationSettings: "'opsz' 64, 'wght' 500", color: "var(--ink)" }}>{children}</h2>
  );
}

function P({ children }) { return <p>{children}</p>; }

export default function Cookies() {
  return (
    <LegalLayout
      eyebrow="vi. Small crumbs"
      title="Cookie"
      italic="policy."
      lastUpdated="July 17, 2026"
      testid="cookie-page"
    >
      <P>
        This page explains what cookies and similar storage we use, and why. PintaFlow does not use advertising cookies, does not sell your data, and does not follow you around the internet.
      </P>

      <H2>1. What is a cookie?</H2>
      <P>
        A cookie is a small text file a website stores in your browser to remember you across pages. Similar technologies (localStorage, sessionStorage) work the same way. We use both, in small amounts, for the reasons below.
      </P>

      <H2>2. Cookies we use</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li>
          <strong style={{ color: "var(--ink)" }}>Session cookie</strong> (<code>session_token</code>) — keeps you signed in for 30 days. Deleted when you sign out. This is <em>strictly necessary</em>.
        </li>
        <li>
          <strong style={{ color: "var(--ink)" }}>Local storage</strong> (<code>session_token</code> backup + offline queue) — lets the PWA function offline and keeps you signed in on mobile. Strictly necessary.
        </li>
        <li>
          <strong style={{ color: "var(--ink)" }}>Cookie banner preference</strong> — remembers whether you clicked &quot;Accept all&quot; or &quot;Essential only&quot;, so we don&apos;t re-ask you.
        </li>
        <li>
          <strong style={{ color: "var(--ink)" }}>PostHog analytics</strong> (optional) — a single anonymous ID that helps us see which features are used. We do not track IP addresses or fingerprint your browser. Loaded <em>only</em> if you click &quot;Accept all&quot;.
        </li>
      </ul>

      <H2>3. What we do NOT use</H2>
      <ul className="list-disc pl-6 space-y-2">
        <li>No Google/Meta advertising cookies.</li>
        <li>No cross-site tracking pixels.</li>
        <li>No data brokers or affiliate networks.</li>
        <li>No fingerprinting scripts.</li>
      </ul>

      <H2>4. Your choices</H2>
      <P>
        On your first visit, we show a cookie banner with two clear options:
      </P>
      <ul className="list-disc pl-6 space-y-2">
        <li><strong style={{ color: "var(--ink)" }}>Essential only</strong> — only the strictly necessary cookies. Analytics is disabled.</li>
        <li><strong style={{ color: "var(--ink)" }}>Accept all</strong> — enables PostHog too, so we can improve the product for everyone.</li>
      </ul>
      <P>
        You can change your mind any time by clearing cookies for pintaflow.com in your browser — the banner will reappear.
      </P>

      <H2>5. Third-party services</H2>
      <P>
        The following third parties may set cookies when you use their features inside PintaFlow:
      </P>
      <ul className="list-disc pl-6 space-y-2">
        <li>Google (only when you sign in with Google OAuth).</li>
        <li>YouTube (only if you connect your channel via Integrations — used by Google&apos;s OAuth flow).</li>
        <li>PostHog (only if you accepted analytics).</li>
      </ul>
      <P>
        Each is governed by its own privacy policy. We do not share your data with any of them beyond what is strictly required to authenticate you.
      </P>

      <H2>6. Do Not Track</H2>
      <P>
        We honour browser &quot;Do Not Track&quot; headers by disabling PostHog for those requests, even if you clicked &quot;Accept all&quot;.
      </P>

      <H2>7. Questions</H2>
      <P>
        Email <a href="mailto:hello@pintaflow.com" style={{ color: "var(--seal)" }}>hello@pintaflow.com</a> — we&apos;re happy to explain any specific cookie you see in your browser inspector.
      </P>
    </LegalLayout>
  );
}
